import { AnswerStatus } from '@/types/answer/AnswerStatus';
import { AppDispatch } from '@/app/store';
import { Box, Container, Grid, useMediaQuery } from '@mui/material';
import { CookieConsentBanner } from '@/components/common/CookieConsentBanner';
import { Footer } from '@/components/main/footer/Footer';
import { LetterSimpleStatus } from '@/types/letter/LetterSimpleStatus';
import { MainNavigation } from '@/components/main/navigation/MainNavigation';
import { MobileHeader } from '@/components/common/MobileHeader';
import { Outlet, useLocation } from 'react-router-dom';
import { ROUTE_MAIN } from '@/app/routes';
import { Theme } from '@mui/material/styles';
import { TopLine } from '@/components/common/TopLine';
import { UserDTO } from '@/types/user/UserDTO';
import { clearSignedOutByUser, getAuthenticated, getCurrentUser, getSignedOutByUser } from '@/services/authSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetLettersCountQuery } from '@/services/api/letterApiSlice';
import React, { FC, Fragment, useEffect } from 'react';

export const MainPage: FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const location = useLocation();

  const isPrerender: boolean = navigator.userAgent === 'ReactSnap' || navigator.userAgent === 'Prerender';
  const isMobile: boolean = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm')) && !isPrerender;

  const isAuthenticated: boolean = useAppSelector(getAuthenticated);
  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);
  const isSignedOutByUser: boolean = useAppSelector(getSignedOutByUser);

  // The sign-out flag redirected this visitor to the main page rather than a login form;
  // clear it on arrival so a later visit to a cabinet address offers the form and the return trip.
  useEffect((): void => {
    if (isSignedOutByUser) {
      dispatch(clearSignedOutByUser());
    }
  }, [dispatch, isSignedOutByUser]);

  const { data: inboxCount } = useGetLettersCountQuery(
    {
      status: LetterSimpleStatus.ALL,
      answerStatus: AnswerStatus.PROCESSED,
      userId: authUser?.id,
    },
    { skip: !authUser },
  );

  const isMainPage: boolean = location.pathname.startsWith(ROUTE_MAIN);

  return (
    <Fragment>
      <TopLine />
      <Container
        sx={(theme: Theme) => ({
          display: 'flex',
          flexDirection: 'column',
          marginTop: '-6px',
          minHeight: '100%',
          [theme.breakpoints.down('sm')]: {
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(5),
            overflow: 'auto',
          },
        })}>
        <Box display={'flex'} flex={1}>
          <Grid container={true} direction={'column'} wrap={'nowrap'} spacing={isMobile ? 2 : 10}>
            <Grid item={true}>
              {isMobile ? (
                <MobileHeader mainPage={true} />
              ) : (
                <MainNavigation profile={!!authUser} isAuthenticated={isAuthenticated} inboxCount={inboxCount} />
              )}
            </Grid>
            <Grid item={true} xs={true}>
              <Outlet />
            </Grid>
            {location.pathname === ROUTE_MAIN && isMobile && (
              <Grid item={true} mt={'25px'}>
                <Footer isMobile={isMobile} isMainPage={isMainPage} />
              </Grid>
            )}
            {!isMobile && (
              <Grid item={true}>
                <Footer isMobile={isMobile} isMainPage={isMainPage} />
              </Grid>
            )}
          </Grid>
        </Box>
        <CookieConsentBanner />
      </Container>
    </Fragment>
  );
};
